import {useCallback, useEffect, useRef, useState} from "react";
import {newFormCase, readFormCases, writeFormCases} from "./formModel";

/**
 * The tests of the form, kept in sync with the YAML of the editor (the text is what's saved and run).
 *
 * The form keeps its own copy of the tests while it edits them: reading them back from the YAML at every
 * keystroke would drop what can't survive a round trip, like a blank line typed at the end of the facts.
 * The YAML is read again only when it changes from outside the form (typed in the YAML mode, the example).
 */
export function useFormCases(input, setInput) {
    const [state, setState] = useState(() => readFormCases(input));
    const casesRef = useRef(state.cases);
    const writtenRef = useRef(input);

    useEffect(() => {
        if (input !== writtenRef.current) {
            writtenRef.current = input;
            const read = readFormCases(input);
            casesRef.current = read.cases;
            setState(read);
        }
    }, [input]);

    const commit = useCallback((cases, {write = true} = {}) => {
        casesRef.current = cases;
        setState(current => ({...current, cases}));
        if (write) {
            const text = writeFormCases(cases);
            writtenRef.current = text;
            setInput(text);
        }
    }, [setInput]);

    const change = useCallback((key, values) => {
        commit(casesRef.current.map(c => c.key === key ? {...c, ...values} : c));
    }, [commit]);

    // Opening or closing a test only changes what the form shows
    const setOpen = useCallback((key, open) => {
        commit(casesRef.current.map(c => c.key === key ? {...c, open} : c), {write: false});
    }, [commit]);

    const setAllOpen = useCallback((open) => {
        commit(casesRef.current.map(c => ({...c, open})), {write: false});
    }, [commit]);

    const add = useCallback(() => {
        const created = newFormCase();
        commit([...casesRef.current, created]);
        return created.key;
    }, [commit]);

    const remove = useCallback((key) => {
        commit(casesRef.current.filter(c => c.key !== key));
    }, [commit]);

    return {...state, change, setOpen, setAllOpen, add, remove};
}
